import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { ManageQuizMarkProvider } from '@/context/quiz-marked/ManageQuizMarkProvider';
import { ErrorBoundaryWrapper } from '@/dev/error-boundary/ErrorBoundaryWrapper';
import MainView from '@/layout/MainView';
import QuizBuilderHeaderLayout from '@/layout/QuizBuilderHeaderLayout';
import AssignmentResolvePage from '@/pages/_main/assignments/AssignmentResolvePage';
import AssignmentsPage from '@/pages/_main/assignments/AssignmentsPage';
import AssignmentSubmissionViewPage from '@/pages/_main/assignments/AssignmentSubmissionViewPage';
import MeetingEventPage from '@/pages/_main/calendar/MeetingEventPage';
import MeetingPage from '@/pages/_main/calendar/MeetingPage';
import ChatIndexPage from '@/pages/_main/chat/ChatIndexPage';
import ChatPage from '@/pages/_main/chat/ChatPage';
import FilePage from '@/pages/_main/files/FilePage';
import FilesPage from '@/pages/_main/files/FilesPage';
import GroupFilesPage from '@/pages/_main/files/GroupFilesPage';
import MyGradesPage from '@/pages/_main/grades/MyGradesPage';
import GroupAttendancePage from '@/pages/_main/groups/GroupAttendancePage';
import GroupGradesPage from '@/pages/_main/groups/GroupGradesPage';
import GroupPage from '@/pages/_main/groups/GroupPage';
import GroupsPage from '@/pages/_main/groups/GroupsPage';
import AssignmentBuilderPage from '@/pages/_main/manage/assignment/AssignmentBuilderPage';
import ManageAssignmentGradePage from '@/pages/_main/manage/assignment/ManageAssignmentGradePage';
import ManageAssignmentPage from '@/pages/_main/manage/assignment/ManageAssignmentPage';
import ManageAssignmentsPage from '@/pages/_main/manage/assignment/ManageAssignmentsPage';
import ManageGroupsPage from '@/pages/_main/manage/group/ManageGroupsPage';
import ManageHolidaysPage from '@/pages/_main/manage/holidays/ManageHolidaysPage';
import ManageLocationsPage from '@/pages/_main/manage/location/ManageLocationsPage';
import ManageParentsPage from '@/pages/_main/manage/parent/ManageParentsPage';
import ManagePostsPage from '@/pages/_main/manage/post/ManagePostsPage';
import ManageQuizMarkPage from '@/pages/_main/manage/quiz/ManageQuizMarkPage';
import ManageQuizPage from '@/pages/_main/manage/quiz/ManageQuizPage';
import ManageQuizzesPage from '@/pages/_main/manage/quiz/ManageQuizzesPage';
import ManageUsersMassImportPage from '@/pages/_main/manage/user/ManageUsersMassImportPage';
import ManageUsersPage from '@/pages/_main/manage/user/ManageUsersPage';
import ParentAssignmentsPage from '@/pages/_main/parent/ParentAssignmentsPage';
import ParentAttendancePage from '@/pages/_main/parent/ParentAttendancePage';
import ParentGradesPage from '@/pages/_main/parent/ParentGradesPage';
import ParentGroupPage from '@/pages/_main/parent/ParentGroupPage';
import ParentGroupsPage from '@/pages/_main/parent/ParentGroupsPage';
import ParentHomePage from '@/pages/_main/parent/ParentHomePage';
import ParentQuizzesPage from '@/pages/_main/parent/ParentQuizzesPage';
import ParentSchedulePage from '@/pages/_main/parent/ParentSchedulePage';
import ParentTeachersPage from '@/pages/_main/parent/ParentTeachersPage';
import PostPage from '@/pages/_main/post/PostPage';
import QuizBuilderBasicInfoPage from '@/pages/_main/quizzes/builder/QuizBuilderBasicInfoPage';
import QuizBuilderContentPage from '@/pages/_main/quizzes/builder/QuizBuilderContentPage';
import QuizBuilderContentQuestionPage from '@/pages/_main/quizzes/builder/QuizBuilderContentQuestionPage';
import QuizBuilderContentSectionPage from '@/pages/_main/quizzes/builder/QuizBuilderContentSectionPage';
import QuizBuilderPage from '@/pages/_main/quizzes/builder/QuizBuilderPage';
import QuizBuilderSettingsPage from '@/pages/_main/quizzes/builder/QuizBuilderSettingsPage';
import QuizDetailPage from '@/pages/_main/quizzes/QuizDetailPage';
import QuizPage from '@/pages/_main/quizzes/QuizPage';
import QuizResultPage from '@/pages/_main/quizzes/QuizResultPage';
import QuizzesPage from '@/pages/_main/quizzes/QuizzesPage';
import SchedulePage from '@/pages/_main/schedule/SchedulePage';
import SettingsPage from '@/pages/_main/settings/SettingsPage';
import LoginPage from '@/pages/auth/login/LoginPage';
import PasswordChangePage from '@/pages/auth/password-change/PasswordChangePage';
import PasswordResetPage from '@/pages/auth/password-reset/PasswordResetPage';
import SignupPage from '@/pages/auth/signup/SignupPage';
import NotAllowedPage from '@/pages/errors/NotAllowedPage';
import NotFoundPage from '@/pages/errors/NotFoundPage';
import { HomeEntry } from '@/routes/HomeEntry';
import { PrivateRoutes } from '@/routes/PrivateRoutes';
import { ProtectedRoutes } from '@/routes/ProtectedRoutes';

const router = createBrowserRouter([
  {
    element: <ErrorBoundaryWrapper />,
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'auth',
        element: <Outlet />,
        children: [
          {
            path: 'signup',
            element: <SignupPage />,
          },
          {
            path: 'passwordChange',
            element: <PasswordChangePage />,
          },
          {
            path: 'reset-password',
            element: <PasswordResetPage />,
          },
        ],
      },
      {
        element: <PrivateRoutes />,
        children: [
          {
            path: '/',
            element: <MainView />,
            children: [
              {
                index: true,
                element: <HomeEntry />,
              },
              {
                path: 'post/:id',
                element: <PostPage />,
              },
              {
                path: 'chat',
                children: [
                  {
                    index: true,
                    element: <ChatIndexPage />,
                  },
                  {
                    path: ':id',
                    element: <ChatPage />,
                  },
                ],
              },
              {
                path: 'calendar',
                element: <ProtectedRoutes userLvls={[UserLvl.ADMIN, UserLvl.TEACHER, UserLvl.STUDENT]} accountTypes={AccountType.User} redirectPath={'/notAllowed'} />,
                children: [
                  {
                    index: true,
                    element: <MeetingEventPage />,
                  },
                  {
                    path: 'meeting/:id',
                    element: <MeetingPage />,
                  },
                ],
              },
              {
                path: 'groups',
                children: [
                  {
                    index: true,
                    element: <GroupsPage />,
                  },
                  {
                    path: ':id',
                    element: <GroupPage />,
                  },
                  {
                    path: ':id/attendance',
                    element: (
                      <ProtectedRoutes userLvls={[UserLvl.ADMIN, UserLvl.TEACHER]} redirectPath={'/notAllowed'}>
                        <GroupAttendancePage />
                      </ProtectedRoutes>
                    ),
                  },
                  {
                    path: ':id/grades',
                    element: <GroupGradesPage />,
                  },
                ],
              },

              {
                path: 'grades',
                element: (
                  <ProtectedRoutes userLvls={UserLvl.STUDENT} redirectPath={'/notAllowed'}>
                    <MyGradesPage />
                  </ProtectedRoutes>
                ),
              },
              {
                path: 'assignments',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <AssignmentsPage />,
                  },
                  {
                    path: ':assignmentId',
                    element: <AssignmentSubmissionViewPage />,
                  },
                  {
                    path: ':assignmentId/submission/:submissionId',
                    element: <AssignmentSubmissionViewPage />,
                  },
                  {
                    path: ':assignmentId/resolve',
                    element: <AssignmentResolvePage />,
                  },
                ],
              },
              {
                path: 'schedule',
                element: (
                  <ProtectedRoutes userLvls={[UserLvl.ADMIN, UserLvl.TEACHER]} redirectPath={'/notAllowed'}>
                    <SchedulePage />
                  </ProtectedRoutes>
                ),
              },
              {
                path: 'quizzes',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <QuizzesPage />,
                  },
                  {
                    path: ':id/attempt/:attemptId',
                    element: <QuizPage />,
                  },
                  {
                    path: ':id/attempt/:attemptId/result',
                    element: <QuizResultPage />,
                  },
                  {
                    path: ':id',
                    element: <QuizDetailPage />,
                  },
                ],
              },
              {
                path: 'manage',
                element: <ProtectedRoutes userLvls={[UserLvl.ADMIN, UserLvl.TEACHER]} redirectPath={'/notAllowed'} />,
                children: [
                  {
                    path: 'users',
                    children: [
                      {
                        index: true,
                        element: <ManageUsersPage />,
                      },
                      {
                        path: 'massImport',
                        element: <ManageUsersMassImportPage />,
                      },
                    ],
                  },
                  {
                    path: 'groups',
                    element: <ManageGroupsPage />,
                  },
                  {
                    path: 'posts',
                    element: <ManagePostsPage />,
                  },
                  {
                    path: 'parents',
                    element: <ManageParentsPage />,
                  },
                  {
                    path: 'locations',
                    element: (
                      <ProtectedRoutes userLvls={[UserLvl.ADMIN]} redirectPath="/notAllowed">
                        <ManageLocationsPage />
                      </ProtectedRoutes>
                    ),
                  },
                  {
                    path: 'holidays',
                    element: (
                      <ProtectedRoutes userLvls={[UserLvl.ADMIN]} redirectPath="/notAllowed">
                        <ManageHolidaysPage />
                      </ProtectedRoutes>
                    ),
                  },
                  {
                    path: 'quizzes',
                    children: [
                      {
                        index: true,
                        element: <ManageQuizzesPage />,
                      },
                      {
                        // edit mode (with quizId param)
                        path: 'builder/:quizId',
                        element: <QuizBuilderPage />,
                        children: [
                          {
                            element: <QuizBuilderHeaderLayout />,
                            children: [
                              {
                                path: 'basic-info',
                                element: <QuizBuilderBasicInfoPage />,
                              },
                              {
                                path: 'questions',
                                element: <QuizBuilderContentPage />,
                              },
                              {
                                path: 'settings',
                                element: <QuizBuilderSettingsPage />,
                              },
                            ],
                          },
                          {
                            path: 'questions/section/:sectionIndex',
                            element: <QuizBuilderContentSectionPage />,
                          },
                          {
                            path: 'questions/section/:sectionIndex/question/:questionIndex',
                            element: <QuizBuilderContentQuestionPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'quizzes/:id',
                    children: [
                      {
                        index: true,
                        element: <ManageQuizPage />,
                      },
                      {
                        path: 'attempt/:attemptId/mark',
                        element: (
                          <ManageQuizMarkProvider>
                            <ManageQuizMarkPage />
                          </ManageQuizMarkProvider>
                        ),
                      },
                    ],
                  },
                  {
                    path: 'assignments',
                    children: [
                      {
                        index: true,
                        element: <ManageAssignmentsPage />,
                      },
                      {
                        path: 'builder',
                        element: <AssignmentBuilderPage />,
                      },
                      {
                        path: 'builder/:assignmentId',
                        element: <AssignmentBuilderPage />,
                      },
                      {
                        path: ':assignmentId',
                        element: <ManageAssignmentPage />,
                      },
                      {
                        path: ':assignmentId/submission/:responseId',
                        element: <ManageAssignmentGradePage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'fs',
                children: [
                  {
                    index: true,
                    element: <FilesPage />,
                  },
                  {
                    path: 'file/:id',
                    element: <FilePage />,
                  },
                  {
                    path: 'dir/:id',
                    element: <FilesPage />,
                  },
                  {
                    path: 'group/:groupId',
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <Navigate to="shared" replace />,
                      },
                      {
                        path: 'dir/:id',
                        element: <GroupFilesPage />,
                      },
                      {
                        path: 'shared',
                        children: [
                          {
                            index: true,
                            element: <GroupFilesPage />,
                          },
                          {
                            path: ':id',
                            element: <GroupFilesPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'shared',
                    children: [
                      {
                        index: true,
                        element: <FilesPage />,
                      },
                      {
                        path: ':id',
                        element: <FilesPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: 'parent',
                element: <ProtectedRoutes userLvls={UserLvl.STUDENT} accountTypes={AccountType.Parent} redirectPath={'/notAllowed'} />,
                children: [
                  {
                    index: true,
                    element: <ParentHomePage />,
                  },
                  {
                    path: 'calendar',
                    element: <MeetingEventPage />,
                  },
                  {
                    path: 'schedule',
                    element: <ParentSchedulePage />,
                  },
                  {
                    path: 'grades',
                    element: <ParentGradesPage />,
                  },
                  {
                    path: 'quizzes',
                    element: <ParentQuizzesPage />,
                  },
                  {
                    path: 'assignments',
                    element: <ParentAssignmentsPage />,
                  },
                  {
                    path: 'attendance',
                    element: <ParentAttendancePage />,
                  },
                  {
                    path: 'groups',
                    element: <ParentGroupsPage />,
                  },
                  {
                    path: 'groups/:id',
                    element: <ParentGroupPage />,
                  },
                  {
                    path: 'teachers',
                    element: <ParentTeachersPage />,
                  },
                ],
              },
              {
                path: '/settings',
                element: <SettingsPage />,
              },
              {
                path: '/notAllowed',
                element: <NotAllowedPage />,
              },
              {
                path: '*',
                element: <NotFoundPage />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);

export default router;
